'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import { useGetMealPlan } from '@/api/use-get-meal-plan';
import { useNavigation } from '@/components/navigation/navigation-context';
import { convertMealPlanResponseToStore } from '@/hooks/ai-planner-db-utils';
import { useAuthStore } from '@/store';
import { useAIPlannerStore } from '@/store/ai-planner';

interface AIPlannerContextType {
  isOpen: boolean;
  openSheet: () => void;
  closeSheet: () => void;
}

const AIPlannerContext = createContext<AIPlannerContextType | undefined>(
  undefined
);

interface AIPlannerProviderProps {
  children: ReactNode;
}

export function AIPlannerProvider({ children }: AIPlannerProviderProps) {
  const isOpen = useAIPlannerStore.use.isSheetOpen();
  const isRegenerating = useAIPlannerStore.use.isRegenerating();
  const currentPlan = useAIPlannerStore.use.currentPlan();
  const storeOpenSheet = useAIPlannerStore.use.openSheet();
  const storeCloseSheet = useAIPlannerStore.use.closeSheet();
  const setPlan = useAIPlannerStore.use.setPlan();
  const preferences = useAIPlannerStore.use.preferences();
  const { setForceHidden } = useNavigation();
  const { user } = useAuthStore();

  // Track if we've fetched on this sheet opening session
  // Prevents duplicate fetches when toggling sheet quickly
  const hasFetchedThisSessionRef = useRef(false);
  const prevIsOpenRef = useRef(false);

  // Fetch meal plan from database (single source of truth)
  // Note: currentPlan is NOT persisted to localStorage anymore
  // We always fetch fresh data from API when sheet opens
  const { data: mealPlanData, refetch } = useGetMealPlan(
    user?.systemToken ?? '',
    {
      enabled: false,
    }
  );

  const openSheet = useCallback(() => {
    storeOpenSheet();
    setForceHidden(true);
  }, [storeOpenSheet, setForceHidden]);

  const closeSheet = useCallback(() => {
    storeCloseSheet();
    setForceHidden(false);
    // Dispatch custom event for external coordination
    const event = new CustomEvent('ai-planner-sheet-closed');
    document.dispatchEvent(event);
  }, [storeCloseSheet, setForceHidden]);

  // EFFECT 1: Handle sheet open/close, navigation sync, and DB fetch
  useEffect(() => {
    // Sync navigation visibility
    setForceHidden(isOpen);

    // Detect sheet opening transition (false -> true)
    const isOpening = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    // Reset session fetch flag when sheet closes
    if (!isOpen) {
      hasFetchedThisSessionRef.current = false;
      return;
    }

    // On sheet open: Fetch from DB only if no current plan and not regenerating
    if (
      isOpening &&
      user?.systemToken &&
      !hasFetchedThisSessionRef.current &&
      !isRegenerating &&
      !currentPlan // Only fetch if no plan exists yet
    ) {
      hasFetchedThisSessionRef.current = true;
      void refetch();
    }
  }, [
    isOpen,
    user?.systemToken,
    refetch,
    setForceHidden,
    isRegenerating,
    currentPlan,
  ]);

  // EFFECT 2: Update store when data is fetched from database
  useEffect(() => {
    // Populate store with API data from DB (unless user is regenerating)
    // Only set if we actually got data from DB AND there's no current plan yet
    // Don't overwrite fresh streaming data with stale cache data
    if (mealPlanData && !isRegenerating && !currentPlan) {
      const convertedPlan = convertMealPlanResponseToStore(
        mealPlanData,
        preferences
      );
      setPlan(convertedPlan);
    }
  }, [mealPlanData, isRegenerating, currentPlan, preferences, setPlan]);

  // EFFECT 3: Handle escape key to close sheet (only when open)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSheet();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeSheet]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ isOpen, openSheet, closeSheet }),
    [isOpen, openSheet, closeSheet]
  );

  return (
    <AIPlannerContext.Provider value={contextValue}>
      {children}
    </AIPlannerContext.Provider>
  );
}

export function useAIPlannerSheet() {
  const context = useContext(AIPlannerContext);
  if (context === undefined) {
    throw new Error(
      'useAIPlannerSheet must be used within an AIPlannerProvider'
    );
  }
  return context;
}
